import { useCallback, useState } from 'react'
import { buildEventReminderSubject, displayEventReminder, getEventReminderTimes } from '../utils/eventReminder'
import { getEventReminderErrorMessage } from '../utils/errorMessages'
import type { ClaimTask } from '../types/task'

// Only failures are reported. A success would say no more than "the form opened" —
// Office.js raises no event when an externally opened New Event form is saved or closed,
// so the pane cannot tell whether the reminder was actually created, and the open form
// is itself the feedback. An error is left standing: it is actionable, and the button
// beside it is the retry.
export interface EventReminderFeedback {
  taskId: string
  status: 'error'
  message: string
}

/**
 * Opens a personal Outlook calendar reminder for a task (MSD-684). Nothing is sent to
 * MC1 — this is a client-side Office.js call only.
 *
 * Feedback carries the task id so the message shows on the card that was clicked, the
 * same shape `useClaimTasks` uses for hold errors.
 */
export function useEventReminder(claimNumber: string) {
  const [pendingTaskId, setPendingTaskId] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<EventReminderFeedback | null>(null)

  const addEventReminder = useCallback(
    async (task: ClaimTask) => {
      setPendingTaskId(task.id)
      setFeedback(null)

      try {
        // A task with no usable target date yields null, which spreads to nothing — the
        // form opens on Outlook's default slot rather than proposing a bogus one, and the
        // user picks the date and time either way.
        const times = getEventReminderTimes(task.targetDate)
        await displayEventReminder({
          subject: buildEventReminderSubject(task, claimNumber),
          ...times,
        })
      } catch (err: unknown) {
        console.error('[EventReminder] Failed to open the new event form:', err)
        setFeedback({ taskId: task.id, status: 'error', message: getEventReminderErrorMessage(err) })
      } finally {
        setPendingTaskId(null)
      }
    },
    [claimNumber]
  )

  return { addEventReminder, pendingTaskId, feedback }
}
